import Info from 'components/form/Info';
import CountryInput from 'components/form/Inputs/CountryInput';
import Radio from 'components/form/Inputs/Radio';
import Textarea from 'components/form/Inputs/Textarea';
import GENDERS, { GenderValues } from 'enums/Genders';
import React, { FC, useCallback } from 'react';
import styled from 'styled-components';
import Button from '../Button';
import Error from '../form/Error';
import { FlexColumn } from '../form/generic';
import Input from '../form/Inputs/Input';
import LabelledCheckbox from '../form/Inputs/LabelledCheckbox';
import Select from '../form/Inputs/Select';
import MailIcon from '../icons/Mail';
import Field from '../profileNavigation/Field';

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 2rem;
  max-width: 60rem;
  button {
    margin-left: 0.5rem;
  }
`;

const Or = styled.div`
  color: var(--gray-500);
  font-size: 0.9rem;
`;

export type State = {
  gender: GenderValues;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  postalCode: string;
  city: string;
  region: string;
  country: string;
  isMetropolitan: boolean;
  dateOfBirth: string;

  organism: string;
  selectOrganism: string;
  priority: boolean;
  presentation: string;
  comments: string;
};

export type Errors = {
  general: boolean;
  email: boolean;
  phone: boolean;
  organism: boolean;
};

export const initialErrors: Errors = {
  general: false,
  email: false,
  phone: false,
  organism: false,
};

type Variant = 'young' | 'mentor' | 'volunteer;';

type Props = {
  state: State;
  setState: React.Dispatch<React.SetStateAction<State>>;
  errors: Errors;
  setErrors: React.Dispatch<React.SetStateAction<Errors>>;
  loading: boolean;
  variant?: Variant;
  adminView?: boolean;
  onSubmit: React.FormEventHandler;
  onCancel: React.MouseEventHandler<HTMLButtonElement>;
};
const UserInformationForm: FC<Props> = ({
  state,
  setState,
  variant,
  adminView,
  errors,
  loading,
  onSubmit,
  onCancel,
}) => {
  const setCountry = useCallback(
    (value: string) => {
      setState({
        ...state,
        country: value,
        isMetropolitan: value === 'France' ? state.isMetropolitan : false,
      });
    },
    [state, setState],
  );

  const handleChangeInput: React.ChangeEventHandler<
    HTMLInputElement & HTMLTextAreaElement
  > = (e) => {
    if (e.target.name === 'organism') {
      return setState({
        ...state,
        organism: e.target.value,
        selectOrganism: '',
      });
    }

    if (e.target.name === 'selectOrganism') {
      return setState({
        ...state,
        organism: '',
        selectOrganism:
          e.target.value === 'INDEPENDANT_selectOrganism'
            ? 'Je suis à mon compte'
            : 'Je suis à la retraite',
      });
    }
    setState({
      ...state,
      [e.target.name]: e.target.value,
    });
  };

  const handleSelectChange: React.ChangeEventHandler<HTMLSelectElement> = (
    e,
  ) => {
    setState({
      ...state,
      [e.target.name]: e.target.value,
    });
  };

  const handleChangeCheckbox: React.ChangeEventHandler<HTMLInputElement> = (
    e,
  ) => {
    if (e.target.name === 'isMetropolitan') {
      return setState({
        ...state,
        isMetropolitan: e.target.checked,
        country: e.target.checked ? 'France' : state.country,
      });
    }
    setState({
      ...state,
      [e.target.name]: e.target.checked,
    });
  };

  return (
    <section>
      {errors.general && (
        <Error style={{ marginBottom: '1rem' }}>Une erreur est survenue.</Error>
      )}
      <form onSubmit={onSubmit}>
        {variant === 'mentor' && adminView && (
          <Field label="Prioritaire">
            <LabelledCheckbox
              label="Ce mentor est prioritaire"
              name="priority"
              checked={state.priority}
              onChange={handleChangeCheckbox}
            />
          </Field>
        )}

        <Field label="Genre">
          <Select
            value={state.gender}
            onChange={handleSelectChange}
            name="gender"
          >
            <option value={'MALE' as GenderValues}>{GENDERS['MALE']}</option>
            <option value={'FEMALE' as GenderValues}>
              {GENDERS['FEMALE']}
            </option>
            <option value={'NOT_SPECIFIED' as GenderValues}>
              {GENDERS['NOT_SPECIFIED']}
            </option>
          </Select>
        </Field>

        <Field label="Nom">
          <Input
            value={state.lastName}
            onChange={handleChangeInput}
            name="lastName"
            required={!adminView}
          />
        </Field>

        <Field label="Prénom">
          <Input
            value={state.firstName}
            onChange={handleChangeInput}
            name="firstName"
            required={!adminView}
          />
        </Field>

        <Field label="Adresse email">
          <Input
            icon={<MailIcon />}
            value={state.email}
            onChange={handleChangeInput}
            name="email"
            type="email"
            required
          />
        </Field>

        {!!adminView && (
          <Field label="Commentaires">
            <FlexColumn gap={0.5}>
              <Info>
                Ce champ ne peut être vu et modifié que par l'administrateur.
              </Info>
              <Textarea
                name="comments"
                value={state.comments}
                onChange={handleChangeInput}
                noEnterFireSubmit
                rows={10}
              />
            </FlexColumn>
          </Field>
        )}

        {variant === 'mentor' && (
          <Field label="Organisme *">
            <FlexColumn gap={0.5}>
              {errors.organism && (
                <Error>Vous devez préciser votre organisme</Error>
              )}
              <Input
                value={state.organism}
                onChange={handleChangeInput}
                name="organism"
                placeholder="Ex: Google"
              />
              <Or>OU</Or>
              <Radio
                label="Je suis à mon compte"
                checked={state.selectOrganism === 'Je suis à mon compte'}
                name="selectOrganism"
                id="INDEPENDANT"
                onChange={handleChangeInput}
              />
              <Radio
                label="Je suis à la retraite"
                checked={state.selectOrganism === 'Je suis à la retraite'}
                name="selectOrganism"
                id="RETIRED"
                onChange={handleChangeInput}
              />
            </FlexColumn>
          </Field>
        )}

        <Field label="Date de naissance">
          <Input
            type="date"
            value={state.dateOfBirth}
            onChange={handleChangeInput}
            name="dateOfBirth"
            required={!adminView}
            placeholder="aaaa-mm-jj (ex: 2022-05-19)"
          />
        </Field>

        <Field label="Numéro de téléphone">
          <Input
            value={state.phone}
            onChange={handleChangeInput}
            name="phone"
            required={!adminView}
          />
        </Field>

        <Field label="Adresse">
          <Input
            value={state.address}
            onChange={handleChangeInput}
            name="address"
            placeholder="Numéro et rue"
          />
        </Field>

        <Field label="Code postal et ville">
          <Input
            value={state.postalCode || ''}
            onChange={handleChangeInput}
            name="postalCode"
            placeholder="Code postal"
            required={!adminView}
          />
          <Input
            value={state.city}
            onChange={handleChangeInput}
            name="city"
            placeholder="Ville"
          />
        </Field>

        <Field label="Région">
          <Input
            value={state.region}
            onChange={handleChangeInput}
            name="region"
            required={!adminView}
          />
        </Field>

        <Field label="Pays">
          <FlexColumn gap={1}>
            <CountryInput value={state.country} setValue={setCountry} />
            <LabelledCheckbox
              label="Je vis en France métropolitaine"
              checked={state.isMetropolitan}
              onChange={handleChangeCheckbox}
              name="isMetropolitan"
            />
          </FlexColumn>
        </Field>
        <Field label="Mon profil">
          <FlexColumn gap={0.5}>
            <Info>Ce champ peut être vu par d'autres utilisateurs.</Info>
            <Textarea
              rows={15}
              placeholder="Décrivez-vous en quelques lignes"
              onChange={handleChangeInput}
              value={state.presentation}
              name="presentation"
              characterLimit={1500}
              noEnterFireSubmit
            />
          </FlexColumn>
        </Field>

        <ButtonContainer>
          <Button variant="outlined" onClick={onCancel}>
            Annuler
          </Button>
          <Button type="submit" color="primary" loading={loading}>
            Enregistrer
          </Button>
        </ButtonContainer>
      </form>
    </section>
  );
};

export default UserInformationForm;
